import {
  Card,
  HStack,
  VStack,
  Text,
  View,
  Badge as GSBadge,
  Divider,
  Pressable
} from "@gluestack-ui/themed";

import { ProposalGroupProps } from "./types";
import { Badge } from "@repo/core/components/core/Badge";
import { BadgeText } from "@gluestack-ui/themed";

import { Icon } from "@repo/core/components/core/Icon";
import { StatusBadge } from "@repo/core/constants/proposals";
import { ProposalCardStatusEnum } from "@repo/core/enums/proposals";
import { EmptyState } from "@repo/core/components/layout/EmptyState";
import { useDimensions } from "@repo/core/hooks/useDimensions";

/**
 * Gets the status badge text, prioritizing API description when available
 */
const getStatusBadgeText = (
  status: ProposalCardStatusEnum,
  statusCode?: string,
  statusDescription?: string
): string => {
  // Check if we have both statusCode and statusDescription (not empty strings)
  if (
    statusCode &&
    statusDescription &&
    statusCode.trim() !== "" &&
    statusDescription.trim() !== ""
  ) {
    // Normalize code to always have 2 digits
    const normalizedCode =
      statusCode.length === 1 ? `0${statusCode}` : statusCode;
    const result = `${normalizedCode} - ${statusDescription}`;
    return result;
  }
  // Fallback to current system
  const fallback =
    StatusBadge[status as keyof typeof StatusBadge]?.text ||
    StatusBadge[ProposalCardStatusEnum.IN_PROGRESS].text;
  return fallback;
};

export const ProposalGroup = ({
  items,
  onPressOption,
  shouldShowOptions
}: ProposalGroupProps) => {
  const { height, isMobile } = useDimensions();

  const headerTop = 450;

  if (!items?.length) {
    return <EmptyState height={height - headerTop} />;
  }

  return (
    <>
      {items?.map((proposal) => (
        <View key={proposal.date} gap="$4" mb="$6">
          <Text fontSize="$xs" color="$textLight600" fontWeight={500}>
            {proposal.date}
          </Text>
          <Card w="$full" mr="$4">
            {proposal?.items.map((item, index) => (
              <View key={index}>
                <VStack alignItems="flex-start">
                  <HStack w="$full" flex={1}>
                    <View flex={1}>
                      <Badge
                        type={
                          StatusBadge[item.status as keyof typeof StatusBadge]
                            ?.type ||
                          StatusBadge[ProposalCardStatusEnum.IN_PROGRESS].type
                        }
                        text={getStatusBadgeText(
                          item.status,
                          item.statusCode,
                          item.statusDescription
                        )}
                      />
                    </View>
                    {(!shouldShowOptions || shouldShowOptions(item)) && (
                      <Pressable
                        flexDirection="row"
                        alignItems="center"
                        onPress={() =>
                          onPressOption?.(
                            item.proposalId,
                            item.statusCode ?? "",
                            item.proposalTypeCode
                          )
                        }
                        justifyContent="center"
                      >
                        <Icon name="ellipsis-vertical" size={16} />
                        {!isMobile ? (
                          <Text
                            pl="$2"
                            fontSize="$xs"
                            fontWeight={600}
                            color="$secondary800"
                          >
                            Opções
                          </Text>
                        ) : null}
                      </Pressable>
                    )}
                  </HStack>
                  <HStack mt="$4" alignItems="center">
                    <GSBadge
                      action="muted"
                      size="sm"
                      height="$6"
                      mr="$2"
                      py="$1"
                      px="$2"
                      bgColor="$backgroundLight100"
                      borderRadius="$sm"
                    >
                      <BadgeText fontWeight={500} fontSize="$xs">
                        {item.proposalId}
                      </BadgeText>
                    </GSBadge>
                    <HStack flexWrap="wrap" overflow="hidden" flex={1}>
                      <Text size="sm" fontWeight={600} color="$textDark">
                        {item.name}
                      </Text>
                    </HStack>
                  </HStack>
                  <HStack alignItems="center" mt="$2">
                    {/* TODO: Add type - the API not return if is FID or CREDIT */}

                    <Text fontSize="$xs" fontWeight={700} color="$textLight600">
                      {item.type ? `${item.type} •` : ""}{" "}
                    </Text>
                    <Text fontSize="$xs" fontWeight={400} color="$textLight700">
                      {item.cardName}
                    </Text>
                  </HStack>
                </VStack>
                {index != proposal?.items.length - 1 ? (
                  <Divider my="$4" />
                ) : null}
              </View>
            ))}
          </Card>
        </View>
      )) || null}
    </>
  );
};
